import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useStores } from "@/store";
import { useTranslation } from "react-i18next";
import RestaurantList from "./components/RestaurantList";
import RestaurantCard from "./components/RestaurantList/card";
import Filters from "./components/Filters";
import Skeleton from "@/ui/skeleton";
import Loading from "@/components/dc-loading";
import { useFilterContext } from "@/ui/head-bar/filter-context";
import { EmptyIcon } from "@/icons";
import { useRequest, useThrottleFn } from "ahooks";
import { getQuery } from "@/service/http";
import CollectionMap from "../browse/components/collection-map";
import { observer } from "mobx-react-lite";
import "./index.less";
import moment from "moment";
import { Footer } from "@/components";
import { Affix } from "antd";

const extendDate = (dateStr) => {
  if (!dateStr) return null;
  const date = moment(dateStr);
  if (!date.isValid()) return null;
  return [
    date.startOf("day").format("YYYY-MM-DD HH:mm:ss"),
    date.endOf("day").format("YYYY-MM-DD HH:mm:ss"),
  ];
};

const useMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { run } = useThrottleFn(
    () => {
      setIsMobile(window.innerWidth <= 768);
    },
    { wait: 500 }
  );

  useEffect(() => {
    window.addEventListener("resize", run);
    return () => {
      window.removeEventListener("resize", run);
    };
  }, [run]);

  return isMobile;
};

function ExplorePage() {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const { BrowseStore } = useStores();
  const {
    state,
    searchTrigger,
    exploreMapMode,
    pageCity,
    searchState,
    setFilterLoading,
    setFilterTotal,
  } = useFilterContext();
  const routerLocation = useLocation();
  const [headBarHeight, setHeadBarHeight] = useState(0);
  const [total, setTotal] = useState(0);
  const [infiniteKey, setInfiniteKey] = useState(0);
  const [restaurantAds, setRestaurantAds] = useState([]);
  const [ads, setAds] = useState([]);
  const [restaurantsList, setRestaurantsList] = useState([]);
  const [isEmpty, setIsEmpty] = useState(false);
  const pageRef = React.useRef(1);
  const requestInFlightRef = React.useRef(false);
  const releaseRequestLock = useCallback(() => {
    if (!requestInFlightRef.current) return;
    const release = () => {
      requestInFlightRef.current = false;
    };
    const schedule =
      typeof window !== "undefined" &&
      typeof window.requestAnimationFrame === "function"
        ? window.requestAnimationFrame
        : (cb) => setTimeout(cb, 0);
    schedule(() => {
      schedule(release);
    });
  }, []);

  const restaurantsWithAds = useMemo(() => {
    const list = Array.isArray(restaurantsList) ? restaurantsList : [];
    const adList = Array.isArray(ads) ? ads : [];
    if (list.length === 0 || adList.length === 0) return list;

    // Every 10 restaurants, attach an ad to the last one (10th/20th/...).
    // `RestaurantList` will render the ad after that restaurant card.
    return list.map((restaurant, index) => {
      const isTenth = (index + 1) % 10 === 0;
      if (!isTenth) return restaurant;

      const groupIndex = Math.floor(index / 10);
      const ad = adList[groupIndex % adList.length];
      if (!ad) return restaurant;

      return { ...(restaurant || {}), ad };
    });
  }, [restaurantsList, ads]);

  const scrollToTop = useCallback(() => {
    if (typeof window === "undefined") return;
    const el = document.querySelector(".expore-page-list");
    if (el && typeof el.scrollTo === "function") {
      el.scrollTo({ top: 0, left: 0, behavior: "auto" });
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  const resetPagination = useCallback(() => {
    pageRef.current = 1;
    setRestaurantsList([]);
    setInfiniteKey((prev) => prev + 1);
    fetchRestaurants();
    if (
      typeof window !== "undefined" &&
      typeof window.requestAnimationFrame === "function"
    ) {
      window.requestAnimationFrame(scrollToTop);
    } else {
      scrollToTop();
    }
  }, [scrollToTop]);

  const paramsBuilder = (params) => {
    const nextParams = {};
    Object.keys(params || {}).forEach((key) => {
      const value = params[key];
      if (Array.isArray(value)) {
        if (value.length > 0) nextParams[key] = value;
        return;
      }
      if (value === "" || value == null) return;
      nextParams[key] = value;
    });
    return nextParams;
  };

  const searchParams = useMemo(() => {
    // Recompute when the URL querystring changes (e.g. head-bar Search in dev mode).
    const _search = routerLocation.search;
    void _search;
    const { q, date, seats, region } = getQuery();
    return { q, date, seats, region };
  }, [routerLocation.search]);

  useEffect(() => {
    resetPagination();
  }, [resetPagination, routerLocation.search, searchTrigger, state]);

  // 获取广告
  useRequest(
    () => {
      setIsEmpty(false);
      BrowseStore.getAds(searchParams.q || undefined, searchParams.region).then(
        (res) => {
          setAds(res || []);
        }
      );
      BrowseStore.getResAds({
        ...paramsBuilder({
          region: searchParams.region,
          collection_id: "",
          type: "restaurant",
          search_word: searchParams.q || "",
          lang: searchParams.lang,
        }),
      }).then((res) => setRestaurantAds(res || []));
    },
    {
      refreshDeps: [routerLocation.search],
    }
  );
  const {
    loading,
    data: restaurants,
    run: fetchRestaurants,
  } = useRequest(
    () => {
      setIsEmpty(false);
      return BrowseStore.getBrowseRestaurants({
        ...paramsBuilder({
          ...state,
          ...searchParams,
          order_by: state.sort_by || "default",
          date: null,
          datetime: extendDate(searchParams.date),
          region: searchParams.region,
          page: pageRef.current,
          per_page: 20,
        }),
      });
    },
    {
      manual: true,
      refreshDeps: [state, routerLocation.search, searchTrigger],
      onBefore: () => {
        requestInFlightRef.current = true;
        setFilterLoading(true);
      },
      onError: () => {
        setFilterLoading(false);
      },
      onSuccess: (data) => {
        const next = Array.isArray(data) ? data : [];
        setTotal(data?.headers?.total_count || 0);
        setFilterLoading(false);
        setFilterTotal(data?.headers?.total_count || 0);
        setIsEmpty(data?.headers?.total_count == 0);
        setRestaurantsList((prev) =>
          pageRef.current == 1 ? next : [...prev, ...next]
        );
      },
    }
  );
  const hasMore = useMemo(() => {
    return restaurants?.headers?.next_page > pageRef.current;
  }, [restaurants]);

  useEffect(() => {
    // Allow the next loadMore once new content has painted.
    if (loading) return;
    releaseRequestLock();
  }, [loading, restaurantsList, releaseRequestLock]);

  useEffect(() => {
    const getHeadBarElement = () => document.querySelector(".ui-head-bar");

    let rafId = null;
    const measure = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const headBarElement = getHeadBarElement();
        if (!headBarElement) {
          setHeadBarHeight(0);
          return;
        }
        const next =
          Math.ceil(headBarElement.getBoundingClientRect().height) -
          (window.innerWidth <= 768 ? 130 : 0);
        setHeadBarHeight((prev) => (prev === next ? prev : next));
      });
    };

    measure();

    const headBarElement = getHeadBarElement();
    const resizeObserver =
      headBarElement && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : null;

    if (resizeObserver && headBarElement)
      resizeObserver.observe(headBarElement);
    window.addEventListener("resize", measure, { passive: true });

    return () => {
      window.removeEventListener("resize", measure);
      if (resizeObserver) resizeObserver.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  const loadMore = useCallback(() => {
    if (!hasMore) return;
    if (requestInFlightRef.current) return;

    requestInFlightRef.current = true;
    pageRef.current += 1;
    fetchRestaurants();
  }, [fetchRestaurants, hasMore]);

  const regionInfo = searchState?.regionInfo;
  const mapRegion = useMemo(() => {
    // CollectionMap requires a non-empty region with country_name/lat/lng.
    return regionInfo || pageCity || null;
  }, [pageCity, regionInfo]);

  return (
    <>
      <div
        className="expore-page"
        style={{ "--explore-headbar-height": `${headBarHeight}px` }}
      >
        {(isMobile ? !exploreMapMode : true) && (
          <div className="expore-page-list">
            {!isMobile && (
              <Affix offsetTop={headBarHeight}>
                <Filters />
              </Affix>
            )}

            {isEmpty && (
              <div className="expore-page-empty">
                <EmptyIcon />
                <p>{t("Header.No results found")}</p>
              </div>
            )}

            {total > 0 && (
              <div className="expore-page-total">
                {total} {t("Header.Restaurants")}
              </div>
            )}

            {total > 0 && (
              <div className="explore-restaurant-list">
                {restaurantAds.map((restaurant) => (
                  <RestaurantCard
                    restaurant={restaurant}
                    isAd={true}
                    key={restaurant?.id + "ad"}
                  />
                ))}
              </div>
            )}

            <RestaurantList
              restaurants={restaurantsWithAds}
              loadMore={loadMore}
              hasMore={hasMore}
              infiniteKey={infiniteKey}
            />
            {loading && (
              <>
                <div className="expore-loading">
                  <Loading red={true} />
                </div>
                <Skeleton></Skeleton>
                <Skeleton></Skeleton>
                <Skeleton></Skeleton>
                <Skeleton></Skeleton>
                <Skeleton></Skeleton>
              </>
            )}
          </div>
        )}

        {(isMobile ? exploreMapMode : true) && (
          <div className="expore-page-map">
            <CollectionMap
              restaurants={restaurantsList}
              region={mapRegion}
              tapMarkers={() => {}}
            />
          </div>
        )}
      </div>
      <Footer isFixed={true} />
    </>
  );
}

export default observer(ExplorePage);
